<template>
  <div>
    <div
      v-if="hasItems || includeFilterTools"
      class="filter-row-flex align-items-center"
    >
      <LegacyDropdown
        v-if="includeFilterTools"
        :options="filterBy"
        :value="selectedFilter.value"
        class="mb-1 filter-dropdown-width"
        :disabled="isFetching"
        @selected="onClickFilter"
      >
        Filter by:<strong class="ml-2">{{ selectedFilter.display }}</strong>
      </LegacyDropdown>
      <LegacyDropdown
        v-if="hasItems"
        :options="sortOptions"
        :value="selectedSort.value"
        class="mb-1"
        :disabled="isFetching"
        @selected="onClickSort"
      >
        Sort by:<strong class="ml-2">{{ selectedSort.display }}</strong>
      </LegacyDropdown>
      <LegacyButton
        v-if="hasItems"
        plain
        no-focus
        small
        normal
        class="show-all-button"
        :disabled="isFetching"
        @click="toggleShowAll"
      >
        {{ showAll ? 'Hide' : 'Show' }} all replies
      </LegacyButton>
      <p
        v-if="!allowCreateItem"
        class="mb-0 ml-auto"
      >
        Showing
        <strong>{{ numVisibleComments }}</strong>
        of
        <strong>{{ commentCount }}</strong>
        {{ `${commentTypeTitleLc}s` }}
      </p>
      <button
        v-if="allowCreateItem && hasItems"
        v-popover="{ name: verifyEmailTooltipName, position: 'top' }"
        class="comment-button btn ml-auto"
        :class="{ 'disabled-button': !isUserVerified }"
        type="button"
        :disabled="isFetching"
        @click="toggleComment"
        @keydown.enter="toggleComment"
      >
        <span>Submit a {{ commentTypeTitleLc }}</span>
      </button>
      <slot v-if="allowCreateItem && !isUserVerified">
        <client-only>
          <v-popover
            class="popover font-size-sm"
            :name="verifyEmailTooltipName"
            event="hover"
            :hide-delay="400"
          >
            Please <a href="/account/email/">verify your email</a> to reply
          </v-popover>
        </client-only>
      </slot>
    </div>

    <div
      v-if="allowCreateItem"
      class="align-items-center d-flex justify-content-between flex-wrap"
      :class="showFilter ? 'mt-2' : 'mb-1 mt-4 mt-md-3'"
    >
      <div v-if="showFilter">
        <div class="d-flex align-items-center">
          <span class="counter-label">
            Unresolved: <strong>{{ unresolvedCount }}</strong>
          </span>
          <span class="counter-label ml-3">
            Pending moderation: <strong>{{ unapprovedCount }}</strong>
          </span>
          <span class="ml-3 p-3">
            <LegacyButton
              plain
              no-focus
              normal
              :disabled="isFetching"
              @click="onClickRefresh"
            >
              Refresh counter
            </LegacyButton>
          </span>
        </div>
      </div>
      <p class="mb-0 ml-auto mt-1 mt-md-0">
        Showing
        <strong>{{ numVisibleComments }}</strong>
        of
        <strong>{{ commentCount }}</strong>
        {{ `${commentTypeTitleLc}s` }}
      </p>
    </div>
  </div>
</template>

<script>
// TODO:BRO-1276: import { DButton, DDropdownItem } from '@appsumo/dorado-nuxt';
import LegacyButton from '../../ui/LegacyButton.vue';
import LegacyDropdown from '../../ui/LegacyDropdown.vue';

export default {
  name: 'PDPFilter',
  components: {
    LegacyButton,
    LegacyDropdown,
  },
  props: {
    showAll: {
      type: Boolean,
    },
    commentTypeTitleLc: {
      type: String,
      required: true,
    },
    showFilter: {
      type: Boolean,
    },
    isUserVerified: {
      type: Boolean,
    },
    isAuthenticated: {
      type: Boolean,
      default: false,
    },
    hasPurchased: {
      type: Boolean,
    },
    hasItems: {
      type: Boolean,
      required: true,
    },
    allowCreateItem: {
      type: Boolean,
      required: true,
    },
    forReviews: {
      type: Boolean,
      required: true,
    },
    numVisibleComments: {
      type: Number,
      required: true,
    },
    isModeratorView: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    let sortOptions;
    const dealsBoughtAndPlusEnabled = this.$store.getters['discussion/isdealsBoughtAndPlusFilterEnabled'];

    if (this.forReviews) {
      sortOptions = [
        { display: 'Newest', value: 'newest' },
        { display: 'Oldest', value: 'oldest' },
        { display: 'Rating (ascending)', value: 'rating-asc' },
        { display: 'Rating (descending)', value: 'rating-desc' },
        { display: 'Helpful', value: 'helpful' },
      ];
    } else {
      sortOptions = [
        { display: 'Latest reply', value: 'latest' },
        { display: 'Helpful', value: 'helpful' },
        { display: 'Newest', value: 'newest' },
        { display: 'Oldest', value: 'oldest' },
      ];
    }

    if (dealsBoughtAndPlusEnabled) {
      sortOptions.push({ display: 'Deals bought', value: 'deals-bought' });
    }

    const filterBy = [
      { display: 'All', value: 'all' },
    ];

    const userIsModerator = this.$store.getters['discussion/userIsModerator'];
    const userIsStaff = this.$store.userSession?.is_staff || false;

    if (userIsModerator || userIsStaff) {
      filterBy.push(
        { display: 'Unresolved', value: 'unresolved' },
        { display: 'Pending moderation', value: 'unapproved' },
      );
    }

    if (dealsBoughtAndPlusEnabled) {
      filterBy.push({ display: 'Plus members', value: 'plus-members' });
    }

    return {
      filterBy,
      sortOptions,
      selectedFilter: filterBy[0],
      selectedSort: sortOptions[0],
    };
  },
  computed: {
    hasFilterApplied() {
      return this.selectedFilter.value !== 'all';
    },
    includeFilterTools() {
      if (!this.showFilter) {
        return false;
      }

      return this.hasItems || this.hasFilterApplied;
    },
    verifyEmailTooltipName() {
      return `filterSubmitButtonTooltip-${this.forReviews ? 'r' : 'q'}`;
    },
    commentCount() {
      return this.forReviews
        ? this.$store.getters['discussion/reviewsAmount']
        : this.$store.getters['discussion/questionsAmount'];
    },
    isFetching() {
      return this.$store.getters['discussion/isFetching'];
    },
    unresolvedCount() {
      return this.forReviews
        ? this.$store.getters['discussion/unresolvedReviews']
        : this.$store.getters['discussion/unresolvedQuestions'];
    },
    unapprovedCount() {
      return this.forReviews
        ? this.$store.getters['discussion/unapprovedReviews']
        : this.$store.getters['discussion/unapprovedQuestions'];
    },
  },
  methods: {
    onClickFilter(valObj) {
      this.selectedFilter = valObj;
      this.$emit('on-change-filter', { type: 'filter', value: valObj.value });
    },
    onClickSort(valObj) {
      this.selectedSort = valObj;
      this.$emit('on-change-filter', { type: 'sort', value: valObj.value });
    },
    toggleShowAll() {
      this.$emit('toggle-show-all');
    },
    toggleComment() {
      if (this.isUserVerified && !this.isFetching) {
        this.$emit('toggle-comment');
      }
    },
    onClickRefresh() {
      if (!this.isFetching) {
        this.$emit('on-click-refresh');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '~/styles/utils/breakpoints';

  strong {
    font-weight: 700;
  }

  .comment-filter {
    border-bottom: 1px solid #e1e1e9;
    margin: 0 !important;

    div:first-child {
      padding-left: 0;

      .row {
        margin-left: 0;
      }
    }

    div:last-child {
      padding-right: 0;
    }
  }

  .counter-label {
    color: #414141;
  }

  .comment-button {
    font-size: 12px;
    padding: 8px 16px;
    font-weight: 700;
    border: 0;
    border-radius: 3px;
    cursor: pointer;
    outline: none;
    background-color: #0275d8;
    color: #fff;

    &:hover,
    &:focus {
      background-color: #0260b2;
      border-color: #025aa5;
      color: #fff;
    }

    &.disabled-button {
      opacity: 0.8;
      cursor: not-allowed;
      background-color: #0275d8;
    }
  }

  .filter-dropdown-width {
    // jl: this is needed for the case when user clicks on 'filter by: all' dropdown.
    // if this style is not present, 'Pending moderation' text will extend beyond the container
    min-width: 130px;
  }

  .filter-row-flex {
    display: flexbox;
    display: flex;
    flex-wrap: wrap;
  }

  .show-all-button {
    font-size: 14px !important;
    padding-left: 12px !important;
    padding-right: 12px !important;
  }

  @include media-breakpoint-down(xs) {
    .end-button-group {
      justify-content: start !important;
      padding-left: 0;
      margin-top: 1rem;
    }
  }
</style>
